import { getMeasureFactor } from "@modules/chefs-assistant/types/measure-unit.type";
import { BaseStore } from "core/base-store";
import { SequenceRepository } from "core/sequence.repository";
import { BehaviorSubject, catchError, first, map, of, zip } from "rxjs";

import { UiService } from "../../../../core/services/ui.services";
import ProductCache from "../../services/product.cache";
import { Product } from "../stock/products/product.model";
import { TechnicalSheet, TechnicalSheetItem } from "./technical-sheet.model";
import TechnicalSheetRepository from "./technical-sheet.repository";

const itemInit: TechnicalSheet = new TechnicalSheet();
const initialItem: TechnicalSheetItem = new TechnicalSheetItem();
type IState = {
  products?: Product[];
  technicalSheets: TechnicalSheet[];
};
const stateInit: IState = {
  technicalSheets: [],
};

export class TechnicalSheetStore extends BaseStore<IState, TechnicalSheet> {
  get sequenceName() {
    return this.repository.collectionName + "-recipeCode";
  }
  seed = 100;
  constructor(
    uiService: UiService,
    private sequence: SequenceRepository,
    private productCache: ProductCache,
    private repository: TechnicalSheetRepository,
  ) {
    super(uiService, stateInit, itemInit);
  }

  private _currentItem: BehaviorSubject<TechnicalSheetItem> = new BehaviorSubject(initialItem);
  get currentItem$() {
    return this._currentItem.asObservable();
  }
  get currentItem() {
    return this._currentItem.value;
  }
  updateCurrentItem(model: TechnicalSheetItem) {
    this._currentItem?.next(model || initialItem);
  }

  loadList() {
    this.ui.loader.show();
    zip(this.productCache.getAll(), this.repository.getAll())
      .pipe(
        catchError(() => zip(of([] as Product[]), of([] as TechnicalSheet[]))),
        map(([products, technicalSheets]) => {
          this._state.next({ products, technicalSheets });
          this.ui.loader.hide();
        }),
        first(),
      )
      .subscribe();
  }

  async save() {
    try {
      const model = this.current;
      const entity = TechnicalSheet.build(model);
      if (!entity.id) {
        entity.recipeCode = await this.sequence.next(this.sequenceName, this.seed);
        this.repository.add(entity);
      } else {
        if (!entity.recipeCode)
          entity.recipeCode = await this.sequence.next(this.sequenceName, this.seed);
        await this.repository.update(entity.id, entity);
      }
      this.ui.modal.close();
      this.loadList();
    } catch (e: any) {
      this.showValidationMessage(e.message);
    }
  }

  delete(id: string) {
    this.repository.delete(id);
    this.loadList();
  }

  getProduct(productId: string) {
    return this.productCache.getById(productId);
  }

  calcGrossCost(p?: Product, item?: TechnicalSheetItem) {
    const productMeasureUnit = p?.measureUnit;
    const recipeMeasureUnit = item?.measureUnit;
    const lastPurchasePrice = p?.lastPurchasePrice ?? 0;
    let productUnit: number = p?.unit ?? 1;
    let recipeQuantity: number = item?.recipeQuantity ?? 0;
    if (productMeasureUnit && recipeMeasureUnit && productMeasureUnit !== recipeMeasureUnit) {
      const productFactor = getMeasureFactor(productMeasureUnit);
      const recipeQuantityFactor = getMeasureFactor(recipeMeasureUnit);
      productUnit = productUnit * productFactor;
      recipeQuantity = recipeQuantity * recipeQuantityFactor;
    }
    const lastPurchaseProductUnitPrice = lastPurchasePrice / productUnit;
    return lastPurchaseProductUnitPrice * recipeQuantity;
  }

  load() {
    const model = this.current;
    if (!model.id) return;
    this.repository.getById(model.id).then(value => {
      const stateUpdated = { ...model, ...value };
      this.updateCurrent({
        ...stateUpdated,
      });
    });
  }
}
